import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { logError } from '@zg-rentals/log-error';
import { Box, Divider, Heading, Paragraph, TextButton } from '@zillow/constellation-10';
import type {
  IdentityVerificationInfo as BackendIdentityVerificationInfo,
  VerifyLandlordIdentityWebApiRequestDto,
} from '@zg-rentals/ts-rent-payment-client';
import UserActions from '@common/actions/UserActions';
import AppConstants from '@common/constants/AppConstants';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import Modal from '@common/components/ConstellationV10Modal';
import DetailsCard from '@rental-manager/components/DetailsCardConstellationV10';
import userPersonas from '@common/constants/userPersonas';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';
import { DEFAULT_ERROR, TAX_ID_ERROR } from '@rental-manager/constants/NotificationConstants';
import { editTaxVerificationPage } from '@rental-manager/actions/OnboardingActions';
import { createMoveInCosts, sendNotification, updateTenantInfo } from '@rental-manager/actions/requests';
import { showModalNotification } from '@common/actions/NotificationActions';
import { useVerifyLandlordIdentity } from '@rental-manager/hooks/useVerifyLandlordIdentity';
import type { MoveInCostsSettingsInterface } from '@rental-manager/pages/Dashboard/types';
import type { MoveInCostsInterface } from '@rental-manager/models/Types';
import paths from '@rental-manager/pathsManifest';

type IdentityVerificationInfo = Omit<BackendIdentityVerificationInfo, 'dobDay' | 'dobMonth' | 'dobYear'> & {
  dobDay: string | number;
  dobMonth: string | number;
  dobYear: string | number;
  selectedTaxVerificationType: string;
};

interface TenantInvitationInfo {
  emailAddress: string;
  tenantFirstName: string;
  tenantLastName: string;
}

interface Props {
  baseMonthlyAmount: Array<string>;
  checkIsPhoneVerificationNeeded: () => Promise<boolean>;
  currentProperty: ZrmClickstreamListing;
  depositAccount: Array<string>;
  editAccountInfo: () => void;
  editDueDate: () => void;
  editIdentityVerification: () => void;
  editMoveInCosts: () => void;
  editPaymentTerm: () => void;
  editRentAmount: () => void;
  editTenantInvitationInfo: () => void;
  getVerificationPayload: () => VerifyLandlordIdentityWebApiRequestDto | null;
  goToPhoneVerificationPage: () => void;
  hasLandlordOnboarded: boolean;
  identityVerificationInfo: IdentityVerificationInfo;
  listingName: string;
  monthlyPaymentDate: Array<string>;
  moveInCosts: Array<string>;
  moveInCostsSettings: MoveInCostsSettingsInterface;
  rentTermsKey: string;
  tenantInvitationInfo: TenantInvitationInfo;
  identityCheckResponse: string;
  onBack: () => void;
  onClose: () => void;
  baseUrl: string;
  propertyId: string;
  checkRequestActionSuccess: (response: unknown) => Promise<unknown>;
  generateMoveInCostPayload: (settings: MoveInCostsSettingsInterface) => Array<MoveInCostsInterface>;
  paymentTerm: Array<string>;
  primaryTenant: Array<string>;
  user: ZrmClickstreamUser;
}

const OnboardingConfirmationPage = ({
  baseMonthlyAmount,
  checkIsPhoneVerificationNeeded,
  currentProperty,
  depositAccount,
  editAccountInfo,
  editDueDate,
  editIdentityVerification,
  editMoveInCosts,
  editPaymentTerm,
  editRentAmount,
  editTenantInvitationInfo,
  getVerificationPayload,
  goToPhoneVerificationPage,
  hasLandlordOnboarded,
  identityVerificationInfo,
  listingName = '',
  monthlyPaymentDate,
  moveInCosts: moveInCostsDisplay,
  moveInCostsSettings,
  rentTermsKey,
  tenantInvitationInfo,
  identityCheckResponse,
  onBack: onBackProp,
  onClose: onCloseProp,
  baseUrl,
  propertyId,
  checkRequestActionSuccess,
  generateMoveInCostPayload,
  paymentTerm,
  primaryTenant,
  user,
}: Props) => {
  const [showLoadingScreen, setShowLoadingScreen] = useState<boolean>(false);
  const dispatch = useDispatch();

  const handlePostVerificationFlow = async () => {
    const settings =
      moveInCostsSettings ??
      ({
        securityDepositSettings: {
          securityDepositPaymentAmount: 0,
          securityDepositNotes: '',
          securityDepositStartsOpen: false,
        },
        proratedRentSettings: {
          proratedRentPaymentAmount: 0,
          proratedRentNotes: '',
          proratedRentStartsOpen: false,
        },
        otherSettings: [],
        dueDate: '',
      } as MoveInCostsSettingsInterface);
    const { securityDepositSettings, proratedRentSettings, otherSettings, dueDate } = settings;
    const moveInCosts = generateMoveInCostPayload(settings);

    const moveInCostsPayload = {
      dueDate,
      moveInCosts,
      rentTermsKey,
      isOnboarding: true,
      identityCheckResponse,
    };

    if (
      proratedRentSettings.proratedRentStartsOpen === true ||
      securityDepositSettings.securityDepositStartsOpen === true ||
      otherSettings.length !== 0
    ) {
      const response = await dispatch(createMoveInCosts({ propertyId, ...moveInCostsPayload }));
      await checkRequestActionSuccess(response);
    }

    const tenant = tenantInvitationInfo ?? {};
    const tenantPayload = {
      rentTermsKey,
      tenantInfo: {
        email: tenant.emailAddress ?? '',
        firstName: tenant.tenantFirstName ?? '',
        lastName: tenant.tenantLastName ?? '',
      },
    };
    const tenantResponse = await dispatch(updateTenantInfo({ ...tenantPayload, propertyId }));
    await checkRequestActionSuccess(tenantResponse);

    dispatch(sendNotification({ propertyId, notificationType: 'rachelPaymentInvite' }));

    try {
      await dispatch(
        UserActions.getUser({
          appName: AppConstants.RENTAL_MANAGER,
          pageWithSensitiveInformation: true,
          includeSummary: false,
          brand: 'zillow',
        }),
      );
    } catch (error) {
      logError({ error: new Error(`Failed to fetch user`) });
    }

    dispatch(push(baseUrl + paths.onboarding.postConfirmationPage));
  };

  const handleVerificationError = (error: unknown) => {
    if ((error as { taxId?: string })?.taxId === 'failed') {
      dispatch(showModalNotification(TAX_ID_ERROR));
      dispatch(editTaxVerificationPage({ propertyId }));
      dispatch(push(baseUrl + paths.onboarding.taxVerificationPage, { fromWhere: 'onboardingReview' }));
      return;
    }
    dispatch(showModalNotification(DEFAULT_ERROR));
  };

  const { mutate: verifyLandlordIdentity } = useVerifyLandlordIdentity({
    postVerificationFlow: handlePostVerificationFlow,
    onError: handleVerificationError,
    onSettled: () => setShowLoadingScreen(false),
    context: 'OnboardingConfirmationPage - verifyLandlordIdentity',
  });

  const onSubmit = async (): Promise<void> => {
    IreneOnboardingCSEvents.emitContinueFromConfirmationPageEvent({ currentProperty, user });
    setShowLoadingScreen(true);

    try {
      const payload = getVerificationPayload();
      if (payload) {
        verifyLandlordIdentity({ postData: payload });
      } else {
        // No identity verification needed — run post-verification flow (move-in costs, tenant, redirect)
        try {
          await handlePostVerificationFlow();
        } catch (flowError) {
          logError({
            error: flowError,
            context: 'OnboardingConfirmationPage - onSubmit (no verification needed)',
          });
          dispatch(showModalNotification(DEFAULT_ERROR));
        } finally {
          setShowLoadingScreen(false);
        }
      }
    } catch (error) {
      logError({ error, context: 'OnboardingConfirmationPage - onSubmit' });
      dispatch(showModalNotification(DEFAULT_ERROR));
      setShowLoadingScreen(false);
    }
  };

  const onBack = (): void => {
    IreneOnboardingCSEvents.emitBackFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: 'invite_tenant',
    });
    onBackProp();
  };

  const onClose = (): void => {
    IreneOnboardingCSEvents.emitExitFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: 'invite_tenant',
    });
    onCloseProp();
  };

  const getTaxVerification = () => {
    const {
      firstName,
      lastName,
      address,
      city,
      state: addressState,
      zip,
      phoneNumber,
      dobMonth,
      dobDay,
      dobYear,
      ssn,
      companyName,
      companyDbaName,
      companyTaxId,
      companyAddress,
      companyCity,
      companyState,
      companyZip,
      selectedTaxVerificationType,
    } = identityVerificationInfo;

    const birthDate = new Date(
      parseInt(String(dobYear), 10),
      parseInt(String(dobMonth), 10) - 1,
      parseInt(String(dobDay), 10),
    );
    const birthDateFormatted = birthDate.toLocaleString('en-us', {
      month: 'long',
      year: 'numeric',
      day: '2-digit',
    });

    const last4DigitSSN = ssn?.slice(-4);
    if (selectedTaxVerificationType === 'single_member_llc') {
      return (
        <>
          <Paragraph>Single LLC</Paragraph>

          <Paragraph>{companyName}</Paragraph>
          <Paragraph>DBA: {companyDbaName || 'N/A'}</Paragraph>
          <Paragraph>EIN: {companyTaxId}</Paragraph>
          <Paragraph>
            {firstName} {lastName}
          </Paragraph>
          <Paragraph>{address}</Paragraph>
          <Paragraph>
            {city}, {addressState} {zip}
          </Paragraph>
          <Paragraph>{phoneNumber}</Paragraph>
          <Paragraph>Date of birth: {birthDateFormatted}</Paragraph>
          <Paragraph>Last 4 digits of SSN: XXX-XX-{last4DigitSSN}</Paragraph>
        </>
      );
    } else if (selectedTaxVerificationType === 'multi_member_llc') {
      return (
        <>
          <Paragraph>Multi LLC or corporation</Paragraph>
          <Paragraph>Business:</Paragraph>
          <Paragraph>{companyName}</Paragraph>
          <Paragraph>DBA: {companyDbaName || 'N/A'}</Paragraph>
          <Paragraph>EIN: {companyTaxId}</Paragraph>
          <Paragraph>{companyAddress}</Paragraph>
          <Paragraph>
            {companyCity}, {companyState} {companyZip}
          </Paragraph>
          <Paragraph>Owner:</Paragraph>
          <Paragraph>
            {firstName} {lastName}
          </Paragraph>
          <Paragraph>{address}</Paragraph>
          <Paragraph>
            {city}, {addressState} {zip}
          </Paragraph>
          <Paragraph>{phoneNumber}</Paragraph>
          <Paragraph>Date of birth: {birthDateFormatted}</Paragraph>
          <Paragraph>Last 4 digits of SSN: XXX-XX-{last4DigitSSN}</Paragraph>
        </>
      );
    } else {
      return (
        <Box>
          <Paragraph>Individual</Paragraph>
          <Paragraph>
            {firstName} {lastName}
          </Paragraph>
          <Paragraph>{address}</Paragraph>
          <Paragraph>
            {city}, {addressState} {zip}
          </Paragraph>
          <Paragraph>{phoneNumber}</Paragraph>
          <Paragraph>Date of birth: {birthDateFormatted}</Paragraph>
          <Paragraph>Last 4 digits of SSN: XXX-XX-{last4DigitSSN}</Paragraph>
        </Box>
      );
    }
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={onClose}
      header={
        <Box css={{ padding: 'token(spacing.sm) !important' }}>
          <TextButton textStyle="body-sm-bold" tone="brand" css={{ float: 'left' }} onClick={onBack}>
            Back
          </TextButton>
          <Heading level={3} textStyle="heading-xs" css={{ display: 'flex', justifyContent: 'center' }}>
            Payment setup
          </Heading>
        </Box>
      }
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'token(spacing.sm) !important',
            padding: 'token(spacing.lg) !important',
          }}
        >
          <Box>
            <Heading level={5}>Confirm info and invite tenant</Heading>
            <Paragraph textStyle="body-lg">
              Please review the information you provided for {listingName} and confirm it&apos;s correct.
            </Paragraph>
          </Box>

          <Box>
            <Divider />
            <DetailsCard className="fs-exclude" title="Monthly rent" onEdit={editRentAmount}>
              {baseMonthlyAmount}
            </DetailsCard>
          </Box>

          <Box>
            <Divider />
            <DetailsCard className="fs-exclude" title="Monthly payment date" onEdit={editDueDate}>
              {monthlyPaymentDate}
            </DetailsCard>
          </Box>

          <Box>
            <Divider />
            <DetailsCard className="fs-exclude" title="Payment period" onEdit={editPaymentTerm}>
              {paymentTerm}
            </DetailsCard>
          </Box>

          <Box>
            <Divider />
            <DetailsCard className="fs-exclude" title="Move-in costs" onEdit={editMoveInCosts}>
              {moveInCostsDisplay}
            </DetailsCard>
          </Box>

          <Box>
            <Divider />
            <DetailsCard title="Primary tenant" className="fs-exclude txtWrap" onEdit={editTenantInvitationInfo}>
              {primaryTenant}
            </DetailsCard>
          </Box>

          {!hasLandlordOnboarded && (
            <Box>
              <Divider />
              <DetailsCard className="fs-exclude" title="Tax information" onEdit={editIdentityVerification}>
                {getTaxVerification()}
              </DetailsCard>
            </Box>
          )}

          <Box>
            <Divider />
            <DetailsCard
              className="fs-exclude"
              title="Deposit account"
              onEdit={() => {
                checkIsPhoneVerificationNeeded()
                  .then((isPhoneVerificationNeeded) => {
                    if (isPhoneVerificationNeeded) {
                      return goToPhoneVerificationPage();
                    } else {
                      return editAccountInfo();
                    }
                  })
                  .catch((error) => {
                    logError({ error, context: 'OnboardingConfirmationPage - DetailsCard - onEdit' });
                    return dispatch(showModalNotification(DEFAULT_ERROR));
                  });
              }}
            >
              {depositAccount}
            </DetailsCard>
          </Box>
        </Box>
      }
      footer={
        <Box css={{ padding: 'token(spacing.sm) !important' }}>
          <DebouncedButton onClick={onSubmit} loading={showLoadingScreen}>
            Confirm and invite tenant
          </DebouncedButton>
        </Box>
      }
    />
  );
};

export default OnboardingConfirmationPage;
